import { useEffect, useState } from "react";
import { Backdrop, Button, Card, CardContent, CardHeader, CircularProgress, FormHelperText, Grid, MenuItem, Select, Skeleton, TextField } from "@mui/material";
import { ServiceApi } from "../../../services/index.service";
import { useSnackbar } from "notistack";
import { useSelector } from "react-redux";

const PromotionalEmail = () => {
    const serviceapi = new ServiceApi();
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(false);
    const [circularLoading, setCircularLoading] = useState(false);
    const { storage } = useSelector((state) => state);
    const [lastEmailSent, setLastEmailSent] = useState("");
    const [emailSentCount, setEmailSentCount] = useState(0);
    const [templatesList, setTemplatesList] = useState([]);
    const [details, setDetails] = useState({
        subject: "",
        message: "",
        template: ""
    });
    const [errors, setErrors] = useState({});
    const [selectedTemplate, setSelectedTemplate] = useState("");

    useEffect(() => {
        if (storage?.store?.intStoreID) {
            setDetails({ subject: '', message: '', template: '' });
            setSelectedTemplate("");
            getPromotionalEmailInfo();
        }
    }, [storage?.store?.intStoreID]);

    const getPromotionalEmailInfo = async () => {
        setLoading(true);
        try {
            const { statusCode, last_email_sent_on, templates, sent_emails_count } = await serviceapi.getPromotionalEmailDetails(storage?.store?.intStoreID);
            if (statusCode === 200) {
                setLastEmailSent(last_email_sent_on);
                setTemplatesList(templates || []);
                setEmailSentCount(sent_emails_count);
            }
        } catch (error) {
            console.log('Error: ', error);
            enqueueSnackbar(error?.response?.data?.message || 'Something went wrong, please try again!', {
                anchorOrigin: { vertical: "top", horizontal: "right" },
                variant: "error"
            });
        }
        finally {
            setLoading(false);
        }
    }

    const handleInputChange = (event, field) => {
        const value = event.target.value;
        if (value) {
            let formErrors = { ...errors };
            delete formErrors[field];
            setErrors({ ...formErrors });
        }
        if (value.length > 1000) return;
        if (field === 'template') setSelectedTemplate("");
        setDetails(prevState => ({ ...prevState, [field]: value }));
    };

    const isValidForm = () => {
        let formErrors = { ...errors };
        if (!details.message) formErrors.message = "Please Enter the Message.";
        else delete formErrors.message;
        if (!details.subject) formErrors.subject = "Please Enter the Subject.";
        else delete formErrors.subject;
        if (!details.template) formErrors.template = "Please Select a Template.";
        else delete formErrors.template;
        setErrors({ ...formErrors });
        if (Object.keys(formErrors).length > 0) return false;
        else return true;
    };

    const handleTemplatePreview = async () => {
        if (!isValidForm()) return;
        setCircularLoading(true);
        try {
            const { statusCode, template } = await serviceapi.getEmailTemplate(storage?.store?.intStoreID, details?.template, details.message);
            if (statusCode === 200) {
                let pageDoc = window.open("", '_blank', "").document;
                pageDoc.body.innerHTML = template;
                setSelectedTemplate(template);
            }
        } catch (error) {
            console.log('Error: ', error);
            enqueueSnackbar(error?.response?.data?.message || 'Something went wrong, please try again!', {
                anchorOrigin: { vertical: "top", horizontal: "right" },
                variant: "error"
            });
        }
        finally {
            setCircularLoading(false);
        }
    }

    const handleSendEmail = async () => {
        if (!isValidForm()) return;
        setCircularLoading(true);
        try {
            const reqPayload = {
                "intStoreID": storage?.store?.intStoreID,
                "subject": details?.subject,
                "message": details?.message,
                "template": selectedTemplate
            };
            const { statusCode, message } = await serviceapi.sendPromotionalEmail(reqPayload);
            if (statusCode === 200) {
                enqueueSnackbar(message || 'Record saved Successfully!', {
                    anchorOrigin: { vertical: "top", horizontal: "right" },
                    variant: "success"
                });
                setDetails({ subject: "", message: "", template: "" });
                getPromotionalEmailInfo();
                setSelectedTemplate("");
            }
        } catch (error) {
            console.log('Error: ', error);
            enqueueSnackbar(error?.response?.data?.message || 'Something went wrong, please try again!', {
                anchorOrigin: { vertical: "top", horizontal: "right" },
                variant: "error"
            });
        }
        finally {
            setCircularLoading(false);
        }
    }

    return (
        <Card className="my-2 bg-white shadow-sm">
            <Backdrop sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }} open={circularLoading}>
                <CircularProgress color="inherit" />
            </Backdrop>
            <CardHeader
                title={
                    <p className="text-[#24211F] text-base font-medium">
                        Store Website - Promotional Email <span className="text-xs font-medium text-gray-500">({emailSentCount >= 2 ? 0 : 2 - emailSentCount} email{emailSentCount !== 1 && 's'} left this month)</span>
                    </p>
                }
                className="border-b"
            />
            <CardContent>
                <Grid container spacing={3} alignItems={"center"}>
                    <Grid item sm={12}>
                        <p className="text-xs font-semibold">Last Email Sent</p>
                        {loading ?
                            <Skeleton className="text-base w-52 mt-0.5" />
                            : <p className="text-sm text-[#24211F] mt-1.5">{lastEmailSent || "--"}</p>}
                    </Grid>
                    {
                        emailSentCount < 2 ?
                            <>
                                <Grid item sm={12}>
                                    <p className="text-xs font-semibold">Subject*</p>
                                    <TextField
                                        fullWidth
                                        id="email-subject"
                                        className="mt-1"
                                        placeholder="Enter your Promotional Subject here"
                                        inputProps={{ className: 'text-sm' }}
                                        value={details?.subject}
                                        onChange={(e) => handleInputChange(e, 'subject')}
                                        error={errors?.subject}
                                        helperText={errors?.subject}
                                    />
                                </Grid>
                                <Grid item sm={12}>
                                    <p className="text-xs font-semibold">Message*</p>
                                    <p className="text-[10px]">Limited to 1000 characters <span className="font-semibold">({1000 - details?.message.length} Characters Left)</span></p>
                                    <TextField
                                        fullWidth
                                        id="email-message"
                                        multiline rows={3}
                                        className="mt-1"
                                        placeholder="Enter Your Promotional Announcement Here"
                                        inputProps={{ style: { boxShadow: 'none' } }}
                                        InputProps={{ className: 'py-2 px-3 text-sm' }}
                                        value={details?.message}
                                        onChange={(e) => handleInputChange(e, 'message')}
                                        error={errors?.message}
                                        helperText={errors?.message}
                                    />
                                </Grid>
                                <Grid item sm={12}>
                                    <p className="text-xs font-semibold">Template*</p>
                                    {loading ?
                                        <Skeleton className="text-5xl w-full -mt-0.5" />
                                        :
                                        <>
                                            <Select
                                                fullWidth
                                                id="email-template"
                                                className="mt-1"
                                                inputProps={{ className: 'py-2 px-3 text-sm' }}
                                                // MenuProps={{ PaperProps: { style: { minWidth: 250 } } }} // To Adjust the Menu width
                                                MenuProps={{
                                                    anchorOrigin: { vertical: "top" },
                                                    transformOrigin: { vertical: "bottom" },
                                                    getContentAnchorEl: null
                                                }}
                                                value={details?.template}
                                                onChange={(e) => handleInputChange(e, 'template')}
                                                displayEmpty
                                            >
                                                <MenuItem disabled value="" >Select Template</MenuItem>
                                                {templatesList.map(({ template_id, template_name }) => <MenuItem key={template_id} value={template_id}>{template_name}</MenuItem>)}
                                            </Select>
                                            {errors?.template && (
                                                <FormHelperText className="mx-3" error>
                                                    {errors.template}
                                                </FormHelperText>)}
                                        </>
                                    }
                                </Grid>
                                <Grid item sm={12} className="flex justify-end">
                                    <Button disabled={loading} variant="outlined" onClick={handleTemplatePreview} className={'capitalize text-primary border-primary'}>
                                        Preview Template
                                    </Button>
                                    {/* Send Email Now => will be enabled only after completing template preview   */}
                                    {selectedTemplate && <Button disabled={loading || emailSentCount >= 2} variant="contained" onClick={handleSendEmail} className={'capitalize text-white bg-primary shadow-none ml-3'} classes={{ disabled: "!opacity-50" }}>
                                        Send Email Now
                                    </Button>}
                                </Grid>
                            </>
                            :
                            <Grid item sm={12} className="flex justify-center">
                                <p className="text-base font-semibold">You Have Reached Your Maximum Monthly Email Limit.</p>
                            </Grid>
                    }
                </Grid>
            </CardContent>
        </Card>
    )
}

export default PromotionalEmail;